import { useState } from "react";
import { ModalShell } from "@/components/Modals/ModalShell";
import { useImportCommit } from "@/hooks/useImport";
import { logEvent } from "@/lib/eventLogger";
import type {
  ImportParseResult,
  ImportSelection,
  ImportAmountUnit,
} from "@/utils/types/flavorImport";
import { IMPORT_AMOUNT_UNITS } from "@/utils/types/flavorImport";
import type { VersionDetail } from "@/utils/types/flavorVersion";

type Props = {
  flavorId: string;
  formulaId: number;
  parseResult: ImportParseResult;
  /** When true, the Replace action is hidden — there's nothing to replace. */
  currentFlaskEmpty: boolean;
  onClose: () => void;
  onCommitSuccess?: (detail: VersionDetail) => void;
};

const DEFAULT_AMOUNT = 1.0;
const DEFAULT_UNIT: ImportAmountUnit = "ppm";

type RowState = {
  selected: boolean;
  amount: number;
  unit: ImportAmountUnit;
  /** True iff the source didn't carry an amount and we defaulted to 1.0. */
  needsAmount: boolean;
};

export function ImportResultsModal({
  flavorId,
  formulaId,
  parseResult,
  currentFlaskEmpty,
  onClose,
  onCommitSuccess,
}: Props) {
  const [rows, setRows] = useState<Map<number, RowState>>(() => {
    return new Map(
      parseResult.insertable.map((r) => [
        r.compoundId,
        {
          selected: true,
          amount: r.suggestedAmount ?? DEFAULT_AMOUNT,
          unit: (r.suggestedUnit as ImportAmountUnit | null) ?? DEFAULT_UNIT,
          needsAmount: r.suggestedAmount == null,
        },
      ]),
    );
  });

  const commit = useImportCommit(flavorId, formulaId);

  const updateRow = (id: number, patch: Partial<RowState>) =>
    setRows((m) => {
      const prev = m.get(id);
      if (!prev) return m;
      return new Map(m).set(id, { ...prev, ...patch });
    });

  const totalSelected = [...rows.values()].filter((s) => s.selected).length;

  const selectedSelections = (): ImportSelection[] =>
    [...rows.entries()]
      .filter(([, s]) => s.selected)
      .map(([compoundId, s]) => ({
        compoundId,
        amountValue: s.amount,
        amountUnit: s.unit,
      }));

  const doCommit = (mode: "insert" | "replace") => {
    if (totalSelected === 0) return;
    if (mode === "replace") {
      const ok = window.confirm(
        `This will overwrite ALL ingredients in your current flask with ${totalSelected} imported compound${
          totalSelected === 1 ? "" : "s"
        }. Replace?`,
      );
      if (!ok) {
        logEvent("flavor_workbench_import_resolved", {
          outcome: "cancelled",
          flavor_id: flavorId,
          formula_id: formulaId,
          item_count: totalSelected,
        });
        return;
      }
    }
    logEvent("flavor_workbench_import_resolved", {
      outcome: mode === "replace" ? "replaced" : "inserted",
      flavor_id: flavorId,
      formula_id: formulaId,
      item_count: totalSelected,
    });
    commit.mutate(
      { parseId: parseResult.parseId, mode, selections: selectedSelections() },
      {
        onSuccess: (newDetail) => {
          onClose();
          onCommitSuccess?.(newDetail);
        },
      },
    );
  };

  const M = parseResult.insertable.length;
  const X = parseResult.mixtures.length;
  const K = parseResult.unidentified.length;
  const total = M + X + K;

  return (
    <ModalShell
      open
      eyebrow="IMPORT REVIEW"
      title={`Imported ${total} ingredient${total === 1 ? "" : "s"}`}
      subtitle={`${M} ready · ${X} mixture${X === 1 ? "" : "s"} · ${K} not found`}
      accentVar="var(--brand-green)"
      width={780}
      onClose={onClose}
    >
      <div className="import-modal-body">
        {M > 0 && (
          <section className="import-section">
            <div className="import-section-head">
              <span className="label">Ready to add</span>
              <span className="import-section-count">{M}</span>
            </div>
            <table className="data-table import-table">
              <thead>
                <tr>
                  <th className="cell-check" />
                  <th>Name</th>
                  <th className="cell-cas">CAS</th>
                  <th className="cell-amount">Amount</th>
                  <th className="cell-unit">Unit</th>
                  <th>Source</th>
                </tr>
              </thead>
              <tbody>
                {parseResult.insertable.map((r) => {
                  const s = rows.get(r.compoundId);
                  if (!s) return null;
                  return (
                    <tr
                      key={r.compoundId}
                      className={s.selected ? undefined : "deselected"}
                    >
                      <td className="cell-check">
                        <input
                          type="checkbox"
                          checked={s.selected}
                          onChange={(e) =>
                            updateRow(r.compoundId, { selected: e.target.checked })
                          }
                          aria-label={`Select ${r.name}`}
                        />
                      </td>
                      <td className="cell-name">
                        {r.name}
                        {s.needsAmount && (
                          <span
                            className="import-warn-dot"
                            title="Default amount used (1.0 ppm) — review before committing"
                            aria-label="needs amount"
                          />
                        )}
                      </td>
                      <td className="cell-cas">{r.cas ?? "—"}</td>
                      <td className="cell-amount">
                        <input
                          type="number"
                          className="import-amount-input"
                          value={s.amount}
                          step="any"
                          min={0}
                          onChange={(e) =>
                            updateRow(r.compoundId, {
                              amount: Number(e.target.value),
                              needsAmount: false,
                            })
                          }
                          aria-label={`Amount for ${r.name}`}
                        />
                      </td>
                      <td className="cell-unit">
                        <select
                          className="import-unit-select"
                          value={s.unit}
                          onChange={(e) =>
                            updateRow(r.compoundId, {
                              unit: e.target.value as ImportAmountUnit,
                            })
                          }
                          aria-label={`Unit for ${r.name}`}
                        >
                          {IMPORT_AMOUNT_UNITS.map((u) => (
                            <option key={u} value={u}>
                              {u}
                            </option>
                          ))}
                        </select>
                      </td>
                      <td className="cell-source" title={r.sourceText}>
                        {r.sourceText}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </section>
        )}

        {X > 0 && (
          <section className="import-section warn">
            <div className="import-section-head">
              <span className="label">Mixtures — cannot be added</span>
              <span className="import-section-count">{X}</span>
            </div>
            <ul className="import-list">
              {parseResult.mixtures.map((m, i) => (
                <li key={i}>
                  <span className="name">{m.name}</span>
                  <span className="meta">individual compounds can't be extracted</span>
                </li>
              ))}
            </ul>
          </section>
        )}

        {K > 0 && (
          <section className="import-section mute">
            <div className="import-section-head">
              <span className="label">Not in our system</span>
              <span className="import-section-count">{K}</span>
            </div>
            <ul className="import-list">
              {parseResult.unidentified.map((u, i) => (
                <li key={i}>
                  <span className="name">{u.sourceText}</span>
                  {u.parsedIdentifiers.cas && (
                    <span className="meta mono">CAS {u.parsedIdentifiers.cas}</span>
                  )}
                </li>
              ))}
            </ul>
            <div className="import-section-footnote">Logged for review.</div>
          </section>
        )}
      </div>

      <div className="import-modal-footer">
        <span className="spacer" />
        {commit.isError && (
          <span className="error-text">Import failed — please try again.</span>
        )}
        <button
          type="button"
          className="btn xs ghost"
          onClick={() => {
            logEvent("flavor_workbench_import_resolved", {
              outcome: "cancelled",
              flavor_id: flavorId,
              formula_id: formulaId,
              item_count: totalSelected,
            });
            onClose();
          }}
          disabled={commit.isPending}
        >
          Cancel
        </button>
        {!currentFlaskEmpty && (
          <button
            type="button"
            className="btn xs replace-action"
            onClick={() => doCommit("replace")}
            disabled={totalSelected === 0 || commit.isPending}
          >
            Replace flask
          </button>
        )}
        <button
          type="button"
          className="btn xs primary"
          onClick={() => doCommit("insert")}
          disabled={totalSelected === 0 || commit.isPending}
        >
          {commit.isPending
            ? "Importing…"
            : currentFlaskEmpty
            ? `Add ${totalSelected} ingredient${totalSelected === 1 ? "" : "s"}`
            : `Insert ${totalSelected}`}
        </button>
      </div>
    </ModalShell>
  );
}
